import { BoxShape, SVG_SHAPES } from './shared/shapes';
import {
  PlaceholderMap,
  contentProblem,
  emptyPlaceholders,
  placeholderKeys,
  plannedText,
  renderOf,
  titleOf,
} from './shared/placeholders';
import {
  CatalogEntry,
  DesignCatalog,
  DesignElement,
  RequiredThings,
  VoucherType,
} from '../../services/voucher-design.service';

/** CSS pixels per PDF point. The canvas scales a pt-sized layer by `scale`. */
export const PX_PER_PT = 96 / 72;

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PageSize {
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export type Handle = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';
export const HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

export interface Issue {
  code: string;
  message: string;
  elementId?: string;
}

export interface Validation {
  errors: Issue[];
  warnings: Issue[];
}

/** Positions and sizes are whole points: a third of a millimetre is fine enough and
 * keeps the inspector free of decimals. */
export function roundPt(value: number): number {
  return Math.round(value);
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** The smallest box a kind may have, in pt. */
export function minSizeFor(entry: CatalogEntry | undefined): Size {
  if (entry?.min_size) return entry.min_size;
  if (entry?.render === 'qr' || entry?.render === 'image') return { width: 40, height: 40 };
  if (entry?.render === 'box') return { width: 10, height: 10 };
  return { width: 20, height: 10 };
}

/** The kinds a design of this type may place: product-only kinds need a product design. */
export function allowedOn(entry: CatalogEntry, voucherType: VoucherType): boolean {
  return voucherType === 'product' || !entry.product_only;
}

/** What a design of this type must carry, for this company's settings: element
 * kinds, and placeholders that must appear in some text. */
export function requiredFor(catalog: DesignCatalog, voucherType: VoucherType): RequiredThings {
  return catalog.required?.[voucherType] ?? { elements: [], placeholders: [] };
}

/** The placeholders of a catalog by key. */
export function placeholderMap(catalog: DesignCatalog | null): PlaceholderMap {
  return new Map((catalog?.placeholders ?? []).map((p) => [p.key, p]));
}

/** Keep a box at least `min` big and entirely on the page. */
export function clampRect(rect: Rect, page: PageSize, min: Size): Rect {
  const width = clamp(rect.width, Math.min(min.width, page.width), page.width);
  const height = clamp(rect.height, Math.min(min.height, page.height), page.height);
  return {
    x: clamp(rect.x, 0, page.width - width),
    y: clamp(rect.y, 0, page.height - height),
    width,
    height,
  };
}

/**
 * Resize `start` by dragging `handle` by (dx, dy) pt. With `lockAspect` the box keeps
 * its ratio; the dominant delta decides and the opposite corner stays put.
 */
export function resizeRect(
  start: Rect,
  handle: Handle,
  dx: number,
  dy: number,
  lockAspect: boolean,
  min: Size,
): Rect {
  let { x, y, width, height } = start;
  if (handle.includes('e')) width = start.width + dx;
  if (handle.includes('s')) height = start.height + dy;
  if (handle.includes('w')) {
    width = start.width - dx;
    x = start.x + dx;
  }
  if (handle.includes('n')) {
    height = start.height - dy;
    y = start.y + dy;
  }
  width = Math.max(width, min.width);
  height = Math.max(height, min.height);
  if (lockAspect && start.width > 0 && start.height > 0) {
    const ratio = start.width / start.height;
    const byWidth = Math.abs(width - start.width) >= Math.abs(height - start.height);
    if (byWidth) height = width / ratio;
    else width = height * ratio;
    if (handle.includes('w')) x = start.x + start.width - width;
    if (handle.includes('n')) y = start.y + start.height - height;
    if (!handle.includes('e') && !handle.includes('w')) x = start.x + (start.width - width) / 2;
    if (!handle.includes('n') && !handle.includes('s')) y = start.y + (start.height - height) / 2;
  }
  return { x, y, width, height };
}

function overlapArea(a: Rect, b: Rect): number {
  const w = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
}

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

function luminance(hex: string): number {
  const channels = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const linear = channels.map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

/** WCAG contrast ratio of two #RRGGBB colours (1 to 21), as the backend computes it. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Average glyph width of the voucher fonts, as a fraction of the font size. */
const AVG_GLYPH_EM = 0.52;
/** Below this the fitted text is hard to read on paper. */
export const READABLE_PT = 7;

export interface CapacityEstimate {
  /** Characters the box holds at the set font size (rough: average glyph width). */
  capacity: number;
  /** The text length the box should be planned for, or null for fixed texts. */
  contentMax: number | null;
  /** The font size the longest text would end up with, or null when it fits as set. */
  shrinksTo: number | null;
}

/**
 * How much text a box holds. The fit script shrinks the font until the text fits,
 * so the question is not whether a long text fits but how small it gets. A rough
 * model (average glyph width, whole lines) is enough to warn before it happens.
 */
export function estimateCapacity(
  element: DesignElement,
  entry: CatalogEntry | undefined,
  known: PlaceholderMap,
): CapacityEstimate | null {
  const font = element.font;
  if (!font || entry?.render !== 'text') return null;
  const lineHeight = font.line_height ?? 1.2;
  const multiline = !!element.wrap;
  const charsAt = (size: number) => {
    const perLine = Math.floor(element.width / (size * AVG_GLYPH_EM));
    const lines = multiline ? Math.max(1, Math.floor(element.height / (size * lineHeight))) : 1;
    return Math.max(0, perLine * lines);
  };
  const capacity = charsAt(font.size);
  // the longest text to expect: own words plus every placeholder at its longest
  const content = element.content ?? '';
  const contentMax = placeholderKeys(content, known).length
    ? plannedText(content, known).length
    : null;
  if (contentMax == null || capacity >= contentMax)
    return { capacity, contentMax, shrinksTo: null };
  // the size at which the longest text fits (continuous model, then checked)
  let size = multiline
    ? Math.sqrt((element.width * element.height) / (AVG_GLYPH_EM * lineHeight * contentMax))
    : Math.min(element.width / (AVG_GLYPH_EM * contentMax), element.height / lineHeight);
  size = Math.min(size, font.size);
  while (size > 1 && charsAt(size) < contentMax) size -= 0.5;
  return { capacity, contentMax, shrinksTo: Math.round(size * 2) / 2 };
}

export type AlignMode = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom';
export type DistributeAxis = 'horizontal' | 'vertical';

/** The box around several rects. */
export function selectionBounds(rects: Rect[]): Rect | null {
  if (rects.length === 0) return null;
  const x = Math.min(...rects.map((r) => r.x));
  const y = Math.min(...rects.map((r) => r.y));
  const right = Math.max(...rects.map((r) => r.x + r.width));
  const bottom = Math.max(...rects.map((r) => r.y + r.height));
  return { x, y, width: right - x, height: bottom - y };
}

/**
 * New positions that align `rects` within `target` (the selection's own bounds for
 * several elements, the page for one), like the align buttons of a slide editor.
 */
export function alignRects(rects: Rect[], mode: AlignMode, target: Rect): Rect[] {
  return rects.map((r) => {
    switch (mode) {
      case 'left':
        return { ...r, x: target.x };
      case 'center':
        return { ...r, x: target.x + (target.width - r.width) / 2 };
      case 'right':
        return { ...r, x: target.x + target.width - r.width };
      case 'top':
        return { ...r, y: target.y };
      case 'middle':
        return { ...r, y: target.y + (target.height - r.height) / 2 };
      case 'bottom':
        return { ...r, y: target.y + target.height - r.height };
    }
  });
}

/** Even gaps between three or more rects along one axis; the outer ones stay. */
export function distributeRects(rects: Rect[], axis: DistributeAxis): Rect[] {
  if (rects.length < 3) return rects;
  const horizontal = axis === 'horizontal';
  const size = (r: Rect) => (horizontal ? r.width : r.height);
  const pos = (r: Rect) => (horizontal ? r.x : r.y);
  const order = [...rects].sort((a, b) => pos(a) - pos(b));
  const first = order[0];
  const last = order[order.length - 1];
  const span = pos(last) + size(last) - pos(first);
  const total = order.reduce((sum, r) => sum + size(r), 0);
  const gap = (span - total) / (order.length - 1);
  let cursor = pos(first);
  const placed = new Map<Rect, number>();
  for (const r of order) {
    placed.set(r, cursor);
    cursor += size(r) + gap;
  }
  return rects.map((r) => (horizontal ? { ...r, x: placed.get(r)! } : { ...r, y: placed.get(r)! }));
}

export interface SnapGuides {
  x: number[]; // vertical guide lines, in pt
  y: number[]; // horizontal guide lines, in pt
}

export interface SnapResult {
  dx: number; // correction to add to the move
  dy: number;
  guides: SnapGuides;
}

/**
 * Smart guides: while a selection moves, its edges and centre lines snap to those
 * of the other elements and of the page when they come within `threshold` pt.
 * Returns the correction and the lines to draw.
 */
export function snapMove(
  moving: Rect,
  others: Rect[],
  page: PageSize,
  threshold: number,
  pageEdges = true,
): SnapResult {
  const xTargets = pageEdges ? [0, page.width / 2, page.width] : [page.width / 2];
  const yTargets = pageEdges ? [0, page.height / 2, page.height] : [page.height / 2];
  for (const r of others) {
    xTargets.push(r.x, r.x + r.width / 2, r.x + r.width);
    yTargets.push(r.y, r.y + r.height / 2, r.y + r.height);
  }
  const xEdges = [moving.x, moving.x + moving.width / 2, moving.x + moving.width];
  const yEdges = [moving.y, moving.y + moving.height / 2, moving.y + moving.height];
  const best = (edges: number[], targets: number[]) => {
    let delta = 0;
    let distance = threshold;
    let line: number | null = null;
    for (const edge of edges) {
      for (const target of targets) {
        const d = Math.abs(target - edge);
        if (d < distance) {
          distance = d;
          delta = target - edge;
          line = target;
        }
      }
    }
    return { delta, line };
  };
  const sx = best(xEdges, xTargets);
  const sy = best(yEdges, yTargets);
  // after the correction, every edge that sits on a target gets a line
  const lines = (edges: number[], targets: number[], delta: number) => [
    ...new Set(targets.filter((t) => edges.some((e) => Math.abs(e + delta - t) < 0.01))),
  ];
  return {
    dx: sx.line === null ? 0 : sx.delta,
    dy: sy.line === null ? 0 : sy.delta,
    guides: {
      x: sx.line === null ? [] : lines(xEdges, xTargets, sx.delta),
      y: sy.line === null ? [] : lines(yEdges, yTargets, sy.delta),
    },
  };
}

/**
 * Smart guides while resizing: the edges the handle moves snap to the edges and
 * centre lines of the other elements and of the page. Returns the snapped rect and
 * the lines to draw.
 */
export function snapResize(
  rect: Rect,
  handle: Handle,
  others: Rect[],
  page: PageSize,
  threshold: number,
  lockAspect = false,
  pageEdges = true,
): { rect: Rect; guides: SnapGuides } {
  if (lockAspect) return snapResizeLocked(rect, handle, others, page, threshold, pageEdges);
  const xTargets = pageEdges ? [0, page.width / 2, page.width] : [page.width / 2];
  const yTargets = pageEdges ? [0, page.height / 2, page.height] : [page.height / 2];
  for (const r of others) {
    xTargets.push(r.x, r.x + r.width / 2, r.x + r.width);
    yTargets.push(r.y, r.y + r.height / 2, r.y + r.height);
  }
  const nearest = (edge: number, targets: number[]) => {
    let best: number | null = null;
    let distance = threshold;
    for (const target of targets) {
      const d = Math.abs(target - edge);
      if (d < distance) {
        distance = d;
        best = target;
      }
    }
    return best;
  };
  const out = { ...rect };
  const guides: SnapGuides = { x: [], y: [] };
  if (handle.includes('e')) {
    const t = nearest(rect.x + rect.width, xTargets);
    if (t !== null) {
      out.width = t - rect.x;
      guides.x.push(t);
    }
  }
  if (handle.includes('w')) {
    const t = nearest(rect.x, xTargets);
    if (t !== null) {
      out.width = rect.x + rect.width - t;
      out.x = t;
      guides.x.push(t);
    }
  }
  if (handle.includes('s')) {
    const t = nearest(rect.y + rect.height, yTargets);
    if (t !== null) {
      out.height = t - rect.y;
      guides.y.push(t);
    }
  }
  if (handle.includes('n')) {
    const t = nearest(rect.y, yTargets);
    if (t !== null) {
      out.height = rect.y + rect.height - t;
      out.y = t;
      guides.y.push(t);
    }
  }
  return { rect: out, guides };
}

/**
 * Resize snapping for a fixed ratio (QR code, logo, product image): only the edge
 * nearest a guide snaps, the other dimension follows the ratio, and the corner or
 * centre the handle does not move stays put.
 */
function snapResizeLocked(
  rect: Rect,
  handle: Handle,
  others: Rect[],
  page: PageSize,
  threshold: number,
  pageEdges = true,
): { rect: Rect; guides: SnapGuides } {
  const xTargets = pageEdges ? [0, page.width / 2, page.width] : [page.width / 2];
  const yTargets = pageEdges ? [0, page.height / 2, page.height] : [page.height / 2];
  for (const r of others) {
    xTargets.push(r.x, r.x + r.width / 2, r.x + r.width);
    yTargets.push(r.y, r.y + r.height / 2, r.y + r.height);
  }
  const ratio = rect.width / rect.height;
  const candidates: { axis: 'x' | 'y'; edge: 'e' | 'w' | 's' | 'n'; at: number; d: number }[] = [];
  const consider = (
    axis: 'x' | 'y',
    edge: 'e' | 'w' | 's' | 'n',
    value: number,
    targets: number[],
  ) => {
    for (const t of targets) {
      const d = Math.abs(t - value);
      if (d < threshold) candidates.push({ axis, edge, at: t, d });
    }
  };
  if (handle.includes('e')) consider('x', 'e', rect.x + rect.width, xTargets);
  if (handle.includes('w')) consider('x', 'w', rect.x, xTargets);
  if (handle.includes('s')) consider('y', 's', rect.y + rect.height, yTargets);
  if (handle.includes('n')) consider('y', 'n', rect.y, yTargets);
  if (candidates.length === 0) return { rect, guides: { x: [], y: [] } };
  const best = candidates.sort((a, b) => a.d - b.d)[0];
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;
  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;
  let width: number;
  let height: number;
  if (best.axis === 'x') {
    width = best.edge === 'e' ? best.at - rect.x : right - best.at;
    height = width / ratio;
  } else {
    height = best.edge === 's' ? best.at - rect.y : bottom - best.at;
    width = height * ratio;
  }
  if (width <= 0 || height <= 0) return { rect, guides: { x: [], y: [] } };
  // the anchor: the opposite edge of each axis the handle moves, else the centre
  const x = handle.includes('w')
    ? right - width
    : handle.includes('e')
      ? rect.x
      : centerX - width / 2;
  const y = handle.includes('n')
    ? bottom - height
    : handle.includes('s')
      ? rect.y
      : centerY - height / 2;
  return {
    rect: { x, y, width, height },
    guides: best.axis === 'x' ? { x: [best.at], y: [] } : { x: [], y: [best.at] },
  };
}

/** Do two rects touch or overlap (the marquee test). */
export function rectsIntersect(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

/** The editor-side checks; the backend repeats them and is the final word. */
/** What the company has uploaded; a logo or banner element without a file prints nothing. */
export interface BrandingFiles {
  logo: boolean;
  banner: boolean;
}

/** The legal minimum validity with and without a printed date (Italy: 1 and 10 years). */
export interface ExpiryRule {
  withDate: number;
  withoutDate: number;
}

export function validateLayout(
  elements: DesignElement[],
  catalog: DesignCatalog | null,
  page: PageSize,
  voucherType: VoucherType = 'money',
  branding: BrandingFiles = { logo: true, banner: true },
  expiry: ExpiryRule | null = null,
): Validation {
  const errors: Issue[] = [];
  const warnings: Issue[] = [];
  if (!catalog) return { errors, warnings };
  const byKind = new Map(catalog.elements.map((e) => [e.kind, e]));
  const known = placeholderMap(catalog);
  const placedKinds = new Set(elements.map((e) => e.kind));
  const usedKeys = new Set(
    elements.flatMap((e) =>
      byKind.get(e.kind)?.render === 'text' ? placeholderKeys(e.content ?? '', known) : [],
    ),
  );
  const label = (e: DesignElement) => titleOf(e, byKind.get(e.kind), known);

  const required = requiredFor(catalog, voucherType);
  for (const kind of required.elements) {
    if (!placedKinds.has(kind)) {
      const entryLabel = byKind.get(kind)?.label ?? kind;
      errors.push({ code: 'missing_required', message: `Pflichtelement fehlt: ${entryLabel}` });
    }
  }
  for (const key of required.placeholders) {
    if (!usedKeys.has(key)) {
      const keyLabel = known.get(key)?.label ?? key;
      errors.push({ code: 'missing_required', message: `Pflichtelement fehlt: ${keyLabel}` });
    }
  }
  if (expiry && expiry.withoutDate > expiry.withDate && !usedKeys.has('expires_at')) {
    warnings.push({
      code: 'no_expiry',
      message: `Ohne Ablaufdatum gelten neue Gutscheine mindestens ${expiry.withoutDate} Jahre (Art. 2946 Codice Civile)`,
    });
  }
  const seen = new Set<string>();
  for (const e of elements) {
    const entry = byKind.get(e.kind);
    const render = renderOf(e, entry);
    if (entry && !allowedOn(entry, voucherType)) {
      errors.push({
        code: 'product_only',
        message: `${label(e)} gibt es nur auf Produkt-Gutscheinen`,
        elementId: e.id,
      });
    }
    if (entry?.singleton) {
      if (seen.has(e.kind)) {
        errors.push({
          code: 'duplicate_singleton',
          message: `${label(e)} darf nur einmal platziert werden`,
          elementId: e.id,
        });
      }
      seen.add(e.kind);
    }
    if (
      e.x < 0 ||
      e.y < 0 ||
      e.x + e.width > page.width + 0.5 ||
      e.y + e.height > page.height + 0.5
    ) {
      errors.push({
        code: 'out_of_bounds',
        message: `${label(e)} liegt außerhalb der Seite`,
        elementId: e.id,
      });
    }
    if (e.font) {
      const { font_size_min, font_size_max } = catalog.limits;
      if (e.font.size < font_size_min || e.font.size > font_size_max) {
        errors.push({
          code: 'font_size',
          message: `${label(e)}: Schriftgröße muss zwischen ${font_size_min} und ${font_size_max} pt liegen`,
          elementId: e.id,
        });
      }
      if (!HEX_COLOR.test(e.font.color)) {
        errors.push({
          code: 'color',
          message: `${label(e)}: Farbe muss ein Hex-Wert wie #1A2B3C sein`,
          elementId: e.id,
        });
      }
      if (render === 'text' && e.height < e.font.size * 1.1) {
        warnings.push({
          code: 'text_too_small',
          message: `${label(e)}: Das Feld ist niedriger als die Schrift, der Text wird verkleinert, bis er hineinpasst`,
          elementId: e.id,
        });
      }
      const capacity = estimateCapacity(e, entry, known);
      if (capacity?.shrinksTo != null && capacity.shrinksTo < READABLE_PT) {
        warnings.push({
          code: 'content_too_long',
          message: `${label(e)}: Der längste zu erwartende Text (${capacity.contentMax} Zeichen) würde auf etwa ${capacity.shrinksTo} pt schrumpfen und wäre kaum lesbar. Bitte das Feld vergrößern.`,
          elementId: e.id,
        });
      }
    }
    if (entry?.render === 'text') {
      const content = (e.content ?? '').trim();
      const problem = contentProblem(content, known);
      const productOnly = placeholderKeys(content, known).find(
        (key) => known.get(key)?.product_only,
      );
      if (!content) {
        errors.push({
          code: 'content',
          message: `${label(e)}: Bitte einen Text eingeben`,
          elementId: e.id,
        });
      } else if (content.length > catalog.limits.content_max) {
        errors.push({
          code: 'content',
          message: `${label(e)}: höchstens ${catalog.limits.content_max} Zeichen`,
          elementId: e.id,
        });
      } else if (problem) {
        errors.push({ code: 'content', message: `${label(e)}: ${problem}`, elementId: e.id });
      } else if (productOnly && voucherType === 'money') {
        errors.push({
          code: 'product_only',
          message: `${label(e)}: ${known.get(productOnly)!.label} gibt es nur auf Produkt-Gutscheinen`,
          elementId: e.id,
        });
      }
      const empty = emptyPlaceholders(content, known);
      if (empty.length && empty.length === placeholderKeys(content, known).length) {
        warnings.push({
          code: 'not_printed',
          message: `${label(e)}: Wird nicht gedruckt, kein Wert für ${empty.map((p) => p.label).join(', ')} in den Einstellungen`,
          elementId: e.id,
        });
      }
    }
    if (entry?.colors) {
      const color = e.qr_color ?? '#000000';
      const background = e.qr_background ?? '#ffffff';
      const minContrast = catalog.qr_min_contrast ?? 3;
      if (!HEX_COLOR.test(color) || !HEX_COLOR.test(background)) {
        errors.push({
          code: 'qr_color',
          message: `${label(e)}: Farben müssen Hex-Werte wie #1A2B3C sein`,
          elementId: e.id,
        });
      } else if (contrastRatio(color, background) < minContrast) {
        errors.push({
          code: 'qr_contrast',
          message: `${label(e)}: zu wenig Kontrast zwischen Code und Hintergrund, der Code ließe sich nicht scannen`,
          elementId: e.id,
        });
      } else if (luminance(color) > luminance(background)) {
        warnings.push({
          code: 'qr_inverted',
          message: `${label(e)}: Heller Code auf dunklem Grund wird nicht von allen Scannern gelesen`,
          elementId: e.id,
        });
      }
    }
    if (entry?.render === 'image' || entry?.render === 'box') {
      const borderMax = catalog.limits.border_width_max ?? 50;
      const radiusMax = catalog.limits.border_radius_max ?? 500;
      const border = e.border_width ?? 0;
      const radius = e.border_radius ?? 0;
      if (border < 0 || border > borderMax || radius < 0 || radius > radiusMax) {
        errors.push({
          code: 'frame',
          message: `${label(e)}: Rahmen höchstens ${borderMax} pt, Ecken höchstens ${radiusMax} pt`,
          elementId: e.id,
        });
      }
      if (border > 0 && !HEX_COLOR.test(e.border_color ?? '')) {
        errors.push({
          code: 'frame_color',
          message: `${label(e)}: Rahmenfarbe muss ein Hex-Wert wie #1A2B3C sein`,
          elementId: e.id,
        });
      }
    }
    if (entry?.render === 'box') {
      if (e.fill != null && !HEX_COLOR.test(e.fill)) {
        errors.push({
          code: 'fill',
          message: `${label(e)}: Füllfarbe muss ein Hex-Wert wie #1A2B3C sein`,
          elementId: e.id,
        });
      } else if (e.fill == null && !(e.border_width ?? 0)) {
        errors.push({
          code: 'empty_box',
          message: `${label(e)}: Ohne Füllung und Rahmen wäre das Rechteck unsichtbar`,
          elementId: e.id,
        });
      }
    }
    if (entry?.image_source === 'library' && !e.image_id) {
      errors.push({
        code: 'image',
        message: `${label(e)}: Bitte ein Bild aus der Bibliothek wählen`,
        elementId: e.id,
      });
    }
    if (entry?.image_source === 'logo' && !branding.logo) {
      warnings.push({
        code: 'missing_logo',
        message: `${label(e)}: Noch kein Logo hochgeladen, die Stelle bleibt leer`,
        elementId: e.id,
      });
    }
    if (entry?.image_source === 'banner' && !branding.banner) {
      warnings.push({
        code: 'missing_banner',
        message: `${label(e)}: Noch kein Banner hochgeladen, die Stelle bleibt leer`,
        elementId: e.id,
      });
    }
  }
  // Overlaps only matter where they hurt reading or scanning: text over text, or
  // text and the QR code. Anything on a rectangle or a picture is a design choice.
  const isText = (e: DesignElement) => byKind.get(e.kind)?.render === 'text';
  const isQr = (e: DesignElement) => byKind.get(e.kind)?.render === 'qr';
  const collides = (a: DesignElement, b: DesignElement) =>
    (isText(a) && isText(b)) || (isText(a) && isQr(b)) || (isQr(a) && isText(b));
  for (let i = 0; i < elements.length; i++) {
    for (let j = i + 1; j < elements.length; j++) {
      if (collides(elements[i], elements[j]) && overlapArea(elements[i], elements[j]) > 4) {
        warnings.push({
          code: 'overlap',
          message: `${label(elements[i])} und ${label(elements[j])} überlappen sich`,
          elementId: elements[j].id,
        });
      }
    }
  }
  return { errors, warnings };
}

/** The backend's classes for an element box (mirror of voucher_html.elements.Element.classes). */
export function elementClasses(element: DesignElement, entry: CatalogEntry | undefined): string {
  const render = renderOf(element, entry);
  const names = ['ve', `ve-${render}`];
  if (render === 'text' || render === 'multiline') names.push(`ve-valign-${element.valign}`);
  if (render === 'image' && element.shape === 'circle') names.push('ve-circle');
  if (render === 'image' && element.fit === 'contain') names.push('ve-fit-contain');
  if (render === 'box' && SVG_SHAPES.has(element.shape as BoxShape)) names.push('ve-shape');
  return names.join(' ');
}

/**
 * The backend's inline style for an element box (mirror of Element.style), without
 * the position: the canvas places the wrapper and the box fills it.
 */
export function elementStyle(
  element: DesignElement,
  entry: CatalogEntry | undefined,
  fontCss: (family: string) => string,
): string {
  let css = `left:0;top:0;width:${element.width.toFixed(2)}pt;height:${element.height.toFixed(2)}pt;`;
  const render = renderOf(element, entry);
  if ((render === 'text' || render === 'multiline') && element.font) {
    const f = element.font;
    css +=
      `font-family:${fontCss(f.family)};font-size:${f.size}pt;` +
      `font-weight:${f.bold ? 'bold' : 'normal'};font-style:${f.italic ? 'italic' : 'normal'};` +
      `line-height:${f.line_height ?? 1.2};color:${f.color};text-align:${element.align};`;
  }
  // a drawn shape paints its own fill and outline (see shapes.ts)
  const drawn = render === 'box' && SVG_SHAPES.has(element.shape as BoxShape);
  if ((render === 'image' || render === 'box') && !drawn) {
    if (element.border_width) {
      css += `border:${element.border_width}pt solid ${element.border_color ?? '#ffffff'};`;
    }
    if (render === 'box' && element.shape === 'ellipse') {
      css += 'border-radius:50%;';
    } else if (element.border_radius && element.shape !== 'circle') {
      css += `border-radius:${element.border_radius}pt;`;
    }
  }
  if (render === 'box' && !drawn) css += `background:${element.fill ?? 'transparent'};`;
  if (element.opacity != null && element.opacity < 1) css += `opacity:${element.opacity};`;
  return css;
}
